import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Bot, Send, Calendar, CreditCard, Shield, Gift, Copy, Check, Activity, Handshake } from "lucide-react";
import DocumentsPanel from "../components/DocumentsPanel";
import BureauTracker from "../components/BureauTracker";
import DisputesPanel from "../components/DisputesPanel";
import SubscriptionPanel from "../components/SubscriptionPanel";
import PaymentsPanel from "../components/PaymentsPanel";
import { ContractsPanel } from "../components/ContractsPanel";

const STARTER_SUGGESTIONS = [
  "What's a good debt-to-income ratio for a mortgage?",
  "How do I dispute an inaccurate collection?",
  "Explain credit utilization in simple terms.",
  "What steps should I take before applying for an auto loan?",
];

function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const link = `${window.location.origin}/?ref=${data.code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("Clipboard write failed:", e);
    }
  };
  return (
    <div className="card-dark p-6" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-4 h-4 text-[var(--if-gold)]" />
        <span className="font-mono-label text-[var(--if-gold)]">Refer a Friend · Earn Credit</span>
      </div>
      <p className="text-sm text-white/60 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
        Friends get <strong className="text-[var(--if-gold)]">${data.discount_usd.toFixed(0)} off</strong> any paid package when they use your link.
      </p>
      <div className="flex items-center gap-2 mb-4">
        <input readOnly value={link} className="if-input flex-1 text-xs !py-2" data-testid="referral-link" />
        <button onClick={copy} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid="referral-copy">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10">
        <div>
          <div className="font-mono-label text-white/50">Code</div>
          <div className="font-display text-lg text-[var(--if-gold)] tracking-widest">{data.code}</div>
        </div>
        <div>
          <div className="font-mono-label text-white/50">Successful</div>
          <div className="font-display text-lg text-white">{data.successful_referrals}</div>
        </div>
        <div>
          <div className="font-mono-label text-white/50">Pending</div>
          <div className="font-display text-lg text-white">{data.pending_referrals}</div>
        </div>
      </div>
    </div>
  );
}

function ChatPanel() {
  const sid = useRef(crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (txt) => {
    const text = (txt ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid.current, message: text });
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: "I couldn't reach the server just now. Please try again." }]);
    } finally { setSending(false); }
  };

  return (
    <div className="card-dark p-6 flex flex-col h-[600px]">
      <div className="flex items-center gap-3 pb-4 border-b border-[var(--if-border)]">
        <div className="w-10 h-10 rounded-full bg-[var(--if-olive)] flex items-center justify-center">
          <Bot className="w-5 h-5 text-[var(--if-gold)]" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg leading-tight">Sergeant Credit</div>
          <div className="text-xs text-[rgba(35,38,28,0.55)]">AI Financial Assistant · GPT-5.2 <span className="dot-pulse"></span></div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 flex flex-col gap-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-[rgba(35,38,28,0.6)] mb-2">Ask me anything about credit, loans, or financial strategy. Try:</p>
            <div className="grid sm:grid-cols-2 gap-2 w-full">
              {STARTER_SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)}
                  className="text-left text-sm p-3 rounded-lg border border-[var(--if-border)] hover:border-[var(--if-gold)] transition-all text-[rgba(35,38,28,0.8)]"
                  data-testid={`chat-suggest-${s.slice(0,16).replace(/\s+/g, '-').toLowerCase()}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"} data-testid={`chat-msg-${m.role}`}>
              {m.content.split("\n").map((line, j) => <p key={`${m.id}-${j}`} className={j ? "mt-2" : ""}>{line}</p>)}
            </div>
          </div>
        ))}
        {sending && (
          <div className="chat-bubble-ai text-sm opacity-70 self-start">Sergeant Credit is thinking…</div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 pt-3 border-t border-[var(--if-border)]">
        <input className="if-input" placeholder="Ask a question…" value={input}
          onChange={(e) => setInput(e.target.value)} data-testid="chat-input" />
        <button type="submit" className="btn-gold !py-3 !px-4" disabled={sending} data-testid="chat-send">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function BookingsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/bookings/me").then(({ data }) => setItems(data)); }, []);
  if (items.length === 0) return (
    <div className="card-dark p-6 text-center text-sm text-[rgba(35,38,28,0.55)]">
      No upcoming sessions. <a href="/booking" className="text-[var(--if-gold)] underline">Book one</a>.
    </div>
  );
  return (
    <div className="flex flex-col gap-3">
      {items.slice(0, 6).map((b) => (
        <div key={b.id} className="card-dark p-4 flex items-center justify-between gap-4" data-testid={`dash-booking-${b.id}`}>
          <div>
            <div className="font-display text-base">{b.service}</div>
            <div className="text-xs text-[rgba(35,38,28,0.6)]">{b.date} · {b.time} CT · <span className="capitalize">{b.meeting_type.replace("_", " ")}</span></div>
          </div>
          <span className="px-2.5 py-1 text-xs rounded-full bg-[rgba(201,168,106,0.12)] text-[var(--if-gold)] capitalize">{b.status}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();

  return (
    <section className="section dark-zone">
      <div className="container-if">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">Mission Control</span>
        </div>
        <h1 className="font-display text-[40px] md:text-[56px] leading-[0.95]">
          Welcome back, <span className="italic text-[var(--if-gold)]">{user?.name?.split(" ")[0] || "Operator"}</span>.
        </h1>
        <p className="mt-3 text-[rgba(35,38,28,0.65)] max-w-xl">
          Your private command center — manage your plan, talk to Sergeant Credit, and review your sessions.
        </p>

        <div className="mt-4">
          <Link to="/insights" className="btn-gold !py-2 !px-4 !text-[12px]" data-testid="view-my-insights">
            <Activity className="w-3.5 h-3.5" /> View My Credit Insights
          </Link>
        </div>

        <div className="mt-10">
          <BureauTracker />
        </div>

        <div className="grid lg:grid-cols-[1.3fr,1fr] gap-6 mt-6">
          <div className="flex flex-col gap-6">
            <ChatPanel />
            <DocumentsPanel />
            <DisputesPanel />
          </div>

          <div className="flex flex-col gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[var(--if-gold)]" />
                <span className="font-mono-label text-[var(--if-gold)]">Your Sessions</span>
              </div>
              <BookingsList />
            </div>

            <ReferralCard />

            <ContractsPanel />

            <div className="card-dark p-5" data-testid="dashboard-marketplace-card">
              <div className="flex items-center gap-2 mb-2">
                <Handshake className="w-4 h-4 text-[var(--if-gold)]" />
                <span className="font-mono-label text-[var(--if-gold)]">Partner Marketplace</span>
              </div>
              <p className="text-sm text-white/60 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
                Loans, business funding, mortgage & auto referrals, insurance, and tax help — vetted partners for your next move.
              </p>
              <Link to="/marketplace" className="btn-outline !py-2 !px-4 !text-[12px]" data-testid="dashboard-marketplace-link">
                Browse Partners
              </Link>
            </div>

            <PaymentsPanel />

            <SubscriptionPanel />
          </div>
        </div>
      </div>
    </section>
  );
}
